import { useQuery } from "@apollo/client";
import { useState } from "react";
import { HiOutlineXMark, HiOutlinePhoto } from "react-icons/hi2";
import { Review } from "../../../../__generated__/graphql";
import ResponsiveCDNImage from "../../../../components/common/image/responsive-cdn-image";
import heroGray from "../../../../assets/images/hero_gray.png";
import { track } from "@amplitude/analytics-browser";
import { PriceLabel } from "./price-label";
import Skeleton from "react-loading-skeleton";
import ReviewDetailModal from "../../../../components/review/review-detail-modal";
import { GET_REVIEW_BY_STORE_ID } from "../../../../api/store-review-queries";
import { truncateText } from "../utils/review-text";

const ReviewCard = ({
  storeId,
  pinnedReview,
  bestReviewId,
  setSelectedReview: setSelectedReviewProp,
}: {
  storeId: number;
  pinnedReview?: { reviewImg: string; reviewContent: string };
  bestReviewId?: number | null;
  setSelectedReview?: (review: any) => void;
}) => {
  const [selectedReviewInternal, setSelectedReviewInternal] =
    useState<Review | undefined>();
  const [selectedPinnedReview, setSelectedPinnedReview] =
    useState<boolean>(false);

  const setSelectedReview = (review: Review | undefined) => {
    if (setSelectedReviewProp) {
      setSelectedReviewProp(review);
    } else {
      setSelectedReviewInternal(review);
    }
  };

  const currentSelectedReview = setSelectedReviewProp
    ? undefined // If prop is used, page.tsx will handle the modal
    : selectedReviewInternal;

  const { data, loading } = useQuery(GET_REVIEW_BY_STORE_ID, {
    variables: { storeId, limit: 20, offset: 0 },
  });

  return (
    <div className="w-full flex rounded-[0.625rem] overflow-hidden border border-[#F9F9F9] bg-white shadow-[0_3px_8px_0_rgba(0,0,0,0.05)]">
      <div className="p-4 w-full">
        {loading ? (
          <div className="overflow-x-scroll flex gap-4">
            {/* 스켈레톤 리뷰 카드들 */}
            {[1, 2, 3].map((index) => (
              <div
                key={index}
                className="flex-shrink-0 w-[180px] h-[250px] rounded-lg border border-gray-200 relative overflow-hidden"
              >
                <Skeleton className="w-full h-full" />
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                  <Skeleton className="w-16 h-4 mb-2" />
                  <Skeleton className="w-24 h-3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <ReviewCardElement
              storeId={storeId}
              review={data?.getReviewByStoreId as Review[]}
              pinnedReview={pinnedReview || null}
              setSelectedReview={setSelectedReview}
              setSelectedPinnedReview={setSelectedPinnedReview}
              bestReviewId={bestReviewId}
            />
          </div>
        )}
      </div>

      {!setSelectedReviewProp && (
        <ReviewDetailModal
          review={currentSelectedReview}
          onClose={() => {
            setSelectedReview(undefined);
            setSelectedPinnedReview(false);
          }}
        />
      )}

      {selectedPinnedReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl w-[90%] max-w-[500px] max-h-[90vh] relative">
            <div className="p-6 overflow-y-auto max-h-[90vh]">
              <div className="flex justify-end items-start gap-2 mb-4">
                <button
                  onClick={() => {
                    setSelectedPinnedReview(false);
                  }}
                  className="text-gray-500 active:text-gray-700"
                >
                  <HiOutlineXMark className="w-6 h-6" />
                </button>
              </div>
              <div className="flex flex-col gap-1">
                <ResponsiveCDNImage
                  src={pinnedReview?.reviewImg}
                  alt="리뷰 이미지"
                  className="w-full h-[300px] object-cover rounded-lg mb-4"
                />
                <span className="text-2xl font-semibold">
                  이만큼이 {pinnedReview?.reviewContent}이에요!
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewCard;

const ReviewCardElement = ({
  storeId,
  review,
  pinnedReview,
  setSelectedReview,
  setSelectedPinnedReview,
  bestReviewId,
}: {
  storeId: number;
  review: Review[];
  pinnedReview: { reviewImg: string; reviewContent: string } | null;
  setSelectedReview: (review: Review | undefined) => void;
  setSelectedPinnedReview: (pinnedReview: boolean) => void;
  bestReviewId?: number | null;
}) => {
  // 작성된 리뷰가 없고 db에도 리뷰가 없으면
  if (
    review?.length > 0 ||
    (pinnedReview &&
      pinnedReview?.reviewImg?.length > 0 &&
      pinnedReview?.reviewContent?.length > 0)
  ) {
    // 베스트 리뷰와 일반 리뷰 분리
    const bestReview = review.find((r) => r.id === bestReviewId);
    const otherReviews = review.filter((r) => r.id !== bestReviewId);

    return (
      <div className="overflow-x-scroll flex gap-4">
        {pinnedReview &&
          pinnedReview?.reviewImg?.length > 0 &&
          pinnedReview?.reviewContent?.length > 0 && (
            <div
              onClick={() => {
                setSelectedPinnedReview(true);
                setSelectedReview(undefined);
              }}
              className="flex-shrink-0 w-[180px] h-[250px] rounded-lg border border-gray-200 relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            >
              <ResponsiveCDNImage
                src={pinnedReview.reviewImg}
                alt="리뷰 이미지"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/100 to-transparent">
                <div className="text-white flex flex-col">
                  <span className="text-2xl font-semibold">이만큼이</span>
                  <span className="text-xl font-semibold">
                    {pinnedReview.reviewContent}이에요!
                  </span>
                </div>
              </div>
            </div>
          )}

        {/* 베스트 리뷰 - 특별한 스타일링 */}
        {bestReview && (
          <div
            key={bestReview?.id}
            onClick={() => {
              track("매장 페이지 리뷰 썸네일 클릭");
              setSelectedReview(bestReview as Review);
            }}
            className="flex-shrink-0 w-[180px] h-[250px] rounded-lg border border-gray-200 relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
          >
            {bestReview?.reviewImages && bestReview.reviewImages.length > 0 ? (
              <div className="relative w-full h-full">
                <ResponsiveCDNImage
                  src={bestReview.reviewImages[0]?.img}
                  alt="베스트 리뷰 이미지"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                <ResponsiveCDNImage
                  src={heroGray}
                  alt="리뷰 이미지"
                  className="w-full h-[100px] object-contain rounded-lg"
                  sizeRatio={0.5}
                />
              </div>
            )}

            {/* 베스트 리뷰 가격 라벨 */}
            {bestReview.reviewImages[0]?.img && storeId !== 247 && (
              <PriceLabel
                price={bestReview.order?.baseAmount || 0}
                storeArea={bestReview.store?.area2 || ""}
                weight={bestReview.id}
              />
            )}

            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
              {/* 베스트 배지 */}
              <div className="inline-block mb-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                👑 BEST
              </div>
              {bestReview.reviewImages &&
                bestReview.reviewImages.length > 1 && (
                  <div className="inline-flex items-center gap-1 mb-1 bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium">
                    <HiOutlinePhoto className="w-3 h-3" />
                    <span>+{bestReview.reviewImages.length - 1}</span>
                  </div>
                )}
              <div className="flex items-center gap-1 mb-2">
                <span className="font-semibold text-white">
                  {truncateText(bestReview?.user?.maskedName as string, 7)}
                </span>
                <span className="text-sm font-medium text-yellow-300">
                  {bestReview?.orderType === "PACK" ? "예약구매" : "실시간구매"}
                </span>
              </div>
              <div className="text-white line-clamp-1">
                {bestReview?.content}
              </div>
            </div>
          </div>
        )}

        {/* 일반 리뷰들 */}
        {otherReviews.map((review, index) => (
          <div
            key={review?.id}
            onClick={() => {
              track("매장 페이지 리뷰 썸네일 클릭");
              setSelectedReview(review as Review);
            }}
            className="flex-shrink-0 w-[180px] h-[250px] rounded-lg border border-gray-200 relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
          >
            {review?.reviewImages && review.reviewImages.length > 0 ? (
              <div className="relative w-full h-full">
                <ResponsiveCDNImage
                  src={review.reviewImages[0]?.img}
                  alt="리뷰 이미지"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                <ResponsiveCDNImage
                  src={heroGray}
                  alt="리뷰 이미지"
                  className="w-full h-[100px] object-contain rounded-lg"
                  sizeRatio={0.5}
                />
              </div>
            )}
            {/* 썸네일 이미지가 있고 처음 3개의 포토리뷰에만 가격 라벨 표시하여 유저가 후킹되게 합니다 */}
            {review.reviewImages[0]?.img && index <= 2 && storeId !== 247 && (
              <PriceLabel
                price={review.order?.baseAmount || 0}
                storeArea={review.store?.area2 || ""}
                weight={review.id}
              />
            )}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
              {review.reviewImages && review.reviewImages.length > 1 && (
                <div className="inline-flex items-center gap-1 mb-1 bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium">
                  <HiOutlinePhoto className="w-3 h-3" />
                  <span>+{review.reviewImages.length - 1}</span>
                </div>
              )}
              <div className="flex items-center gap-1 mb-2">
                <span className="font-semibold text-white">
                  {truncateText(review?.user?.maskedName as string, 7)}
                </span>
                <span className="text-sm font-medium text-white">
                  {review?.orderType === "PACK" ? "예약구매" : "실시간구매"}
                </span>
              </div>
              <div className="text-white line-clamp-1">{review?.content}</div>
            </div>
          </div>
        ))}
      </div>
    );
  } else {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] bg-gray-50 rounded-lg">
        <p className="text-gray-500 text-lg text-center">
          아직 리뷰가 없어요.
          <br />
          첫 리뷰를 남겨보세요!
        </p>
      </div>
    );
  }
};
